import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Chip,
  Fab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
} from "@mui/material";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import ResponsiveIframe from "components/ResponsiveIframe";
import SnackbarMessage from "components/SnackbarMessage";
import { FrequencyTypeIds } from "constants/FrequencyTypeConstants";
import {
  NotificationChannelIds,
  channelChipColor,
} from "constants/NotificationChannelConstants";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { EmailThemeRequest } from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { Severity, SnackbarMessageProps } from "types/common-types";
import { convertToZonedDateTime } from "utils/dateUtils";
import translateError from "utils/translateError";
import InAppPreview from "./InAppPreview";

const styles = {
  titleColumn: { width: "33%", fontWeight: "bold" },
  monoValue: { fontFamily: "monospace", color: "text.secondary" },
};

const NotificationConfigurationDetails = () => {
  const { formatMessage: t } = useIntl();
  const {
    notificationConfigurationsUrl,
    notificationConfigurationEditUrl,
    notificationsConfigurationsService,
    notificationService,
    applicationService,
  } = useGlobalContext();

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const { registerPromise } = useTrashablePromises();
  const { organizationId } = contextStore.useState();

  const { data, isLoading, isError } = useQuery(
    ["notificationsConfigurationsService.getNotificationConfiguration", id],
    () => notificationsConfigurationsService.getNotificationConfiguration(id!),
    { enabled: !!id },
  );

  const config = data?.data;
  useTitle(
    config?.triggerName || t({ id: "notificationConfigurationDetails" }),
  );

  const applicationQuery = useQuery(
    ["applicationService.getApplication", config?.applicationId],
    () => applicationService.getApplication(config!.applicationId!),
    { enabled: !!config?.applicationId },
  );

  const themeQuery = useQuery(
    ["notificationService.getEmailThemeByOrganization", config?.organizationId],
    () =>
      notificationService.getEmailThemeByOrganization(
        EmailThemeRequest.fromJS({
          organizationId: config?.organizationId,
        }),
      ),
    {
      enabled:
        !!config?.organizationId &&
        config?.routingTypeId?.toLowerCase() === NotificationChannelIds.Email,
    },
  );

  if (!id) return <Navigate to={notificationConfigurationsUrl()} replace />;
  if (isLoading || themeQuery.isLoading || applicationQuery.isLoading)
    return <Loader />;
  if (isError || !config) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="fetchError" />
        </Alert>
      </Box>
    );
  }

  const isEmail =
    config.routingTypeId?.toLowerCase() === NotificationChannelIds.Email;
  const isInApp = !isEmail;
  const isDisabled = config.organizationId !== organizationId;
  const inheritedTooltip = isDisabled
    ? t(
        { id: "notificationConfigurationInheritedTooltip" },
        { organizationName: config.organizationName },
      )
    : "";

  const handleDelete = async () => {
    try {
      await registerPromise(
        notificationsConfigurationsService.deleteNotificationConfiguration(id!),
      );
      queryClient.invalidateQueries(
        "notificationsConfigurationsService.getNotificationsConfigurations",
      );
      deleteOpenActions.setFalse();
      navigate(notificationConfigurationsUrl());
    } catch (error: any) {
      deleteOpenActions.setFalse();
      setSnackbarMessage(
        new SnackbarMessageProps(translateError(error, t), Severity.Error),
      );
    }
  };

  const rows = [
    {
      title: t({ id: "trigger" }),
      component: <span>{config.triggerName}</span>,
      show: true,
    },
    {
      title: t({ id: "eventKey" }),
      component: (
        <span style={{ fontFamily: "monospace" }}>
          {config.triggerEventKey}
        </span>
      ),
      show: true,
    },
    {
      title: t({ id: "condition" }),
      component: <span>{config.condition}</span>,
      show: !!config.condition,
    },
    {
      title: t({ id: "channel" }),
      component: (
        <Chip
          label={config.channel}
          size="small"
          variant="outlined"
          color={channelChipColor(config.routingTypeId)}
        />
      ),
      show: true,
    },
    {
      title: t({ id: "displayMode" }),
      component: <span>{config.displayModeName}</span>,
      show: isInApp,
    },
    {
      title: t({ id: "statusLabel" }),
      component: (
        <Chip
          label={config.isEnabled ? t({ id: "sent" }) : t({ id: "draft" })}
          size="small"
          variant="outlined"
          color={config.isEnabled ? "success" : "default"}
        />
      ),
      show: true,
    },
    {
      title: t({ id: "organization" }),
      component: <span>{config.organizationName}</span>,
      show: true,
    },
    {
      title: t({ id: "applicationLabel" }),
      component: <span>{applicationQuery?.data?.data?.name}</span>,
      show: true,
    },
    {
      title: t({ id: "emailTheme" }),
      component: (
        <span>{themeQuery?.data?.data?.inheritedOrganizationName}</span>
      ),
      show: isEmail,
    },
    {
      title: t({ id: "templateName" }),
      component: <span>{config.templateName}</span>,
      show: true,
    },
    {
      title: t({ id: "titleSubject" }),
      component: <span>{config.templateTitle || config.subject}</span>,
      show: true,
    },
    {
      title: t({ id: "senderName" }),
      component: <span>{config.senderName}</span>,
      show: isEmail,
    },
    {
      title: t({ id: "senderEmail" }),
      component: <span>{config.senderEmail}</span>,
      show: isEmail,
    },
    {
      title: t({ id: "ccEmails" }),
      component: <span>{config.ccEmails}</span>,
      show: isEmail && !!config.ccEmails,
    },
    {
      title: t({ id: "bccEmails" }),
      component: <span>{config.bccEmails}</span>,
      show: isEmail && !!config.bccEmails,
    },
    {
      title: t({ id: "actionUrl" }),
      component: (
        <span style={{ fontFamily: "monospace" }}>{config.actionUrl}</span>
      ),
      show: isInApp && !!config.actionUrl,
    },
    {
      title: t({ id: "actionLabel" }),
      component: <span>{config.actionLabel}</span>,
      show: isInApp && !!config.actionLabel,
    },
    {
      title: t({ id: "frequency" }),
      component:
        config.frequencyTypeId === FrequencyTypeIds.RepeatEvery ? (
          <span>
            Every {config.frequencyInterval} {config.intervalTypeName}
          </span>
        ) : (
          <span>{config.frequencyTypeName}</span>
        ),
      show:
        isInApp &&
        (config.frequencyTypeId === FrequencyTypeIds.RepeatEvery
          ? !!config.frequencyInterval
          : !!config.frequencyTypeName),
    },
    {
      title: t({ id: "dismissal" }),
      component: <span>{config.dismissalTypeName}</span>,
      show: isInApp && !!config.dismissalTypeName,
    },
    {
      title: t({ id: "validFrom" }),
      component: <span>{convertToZonedDateTime(config.validFrom)}</span>,
      show: isInApp && !!config.validFrom,
    },
    {
      title: t({ id: "expiresOn" }),
      component: <span>{convertToZonedDateTime(config.expiresOn)}</span>,
      show: isInApp && !!config.expiresOn,
    },
  ].filter((r) => r.show);

  return (
    <>
      <RedirectBackButton
        redirectRoute={notificationConfigurationsUrl()}
        labelKey="backToNotifications"
      />
      <h2>{config.triggerName}</h2>

      <Box my={3} display="flex" justifyContent="center">
        <Tooltip title={inheritedTooltip}>
          <span>
            {isDisabled ? (
              <Fab color="primary" disabled>
                <EditIcon />
              </Fab>
            ) : (
              <Link
                to={notificationConfigurationEditUrl(
                  config.notificationJobRoutingId,
                )}
              >
                <Fab color="primary">
                  <EditIcon />
                </Fab>
              </Link>
            )}
          </span>
        </Tooltip>
        <Box ml={4} />
        <Tooltip title={inheritedTooltip}>
          <span>
            <Fab
              color="secondary"
              onClick={deleteOpenActions.setTrue}
              disabled={isDisabled}
            >
              <DeleteIcon />
            </Fab>
          </span>
        </Tooltip>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {rows.map(({ title, component }) => (
              <TableRow key={title}>
                <TableCell sx={styles.titleColumn}>{title}</TableCell>
                <TableCell>{component}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <h2>{t({ id: "preview" })}</h2>

      {isEmail && config.subject && (
        <TableContainer component={Paper}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell>
                  <ResponsiveIframe
                    srcDoc={config.body || ""}
                    title="emailNotificationPreview"
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {isInApp && (
        <InAppPreview
          displayModeId={config.displayModeId}
          title={config.templateTitle}
          body={config.body}
          actionUrl={config.actionUrl}
          actionLabel={config.actionLabel}
          fullWidth
        />
      )}

      <DeleteEntityDialog
        dialogTitleKey="notificationConfigurationDeleteTitle"
        contentKey="notificationConfigurationDeleteQuestion"
        open={deleteOpen}
        onCancel={deleteOpenActions.setFalse}
        handleDelete={handleDelete}
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default NotificationConfigurationDetails;
